import { useTranslations } from 'next-intl';
import ProgressCircular from '../general/ProgressCircular';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { reportAutocomplete } from '@/api';
import { TaxonomyModel } from '@/types';
import Link from '../general/Link';
import { useRouter } from 'next/router';
import { UserContext } from '../ContextProviders';
import SearchNoResult from './SearchNoResult';
import { RecommendationsContext } from '..';

type AutocompleteContentType = {
  searchTerm: string;
};

const AutocompleteContent = (data: AutocompleteContentType) => {
  const { searchTerm } = data;
  const [loading, setLoading] = useState(false);
  const [topicSuggestions, setTopicSuggestions] = useState<{ text: string; value: TaxonomyModel; count: number }[]>([]);
  const [autocompleteWords, setAutocompleteWords] = useState<{ text: string; value: string }[]>([]);
  const [spellCheck, setSpellCheck] = useState<string[]>([]);

  const user = useContext(UserContext);
  const recommendations = useContext(RecommendationsContext);

  const typingTimer = useRef<NodeJS.Timeout | null>(null);
  const t = useTranslations();
  const router = useRouter();

  useEffect(() => {
    if (typingTimer.current) {
      clearTimeout(typingTimer.current);
      setLoading(false);
    }
    if (searchTerm) {
      setLoading(true);

      typingTimer.current = setTimeout(() => {
        reportAutocomplete(searchTerm)
          .then((response) => {
            setTopicSuggestions(
              response.topFacets.map((item) => ({
                text: t('search.$searchsuggestionsskeleton', {
                  search: `<span class="text-[#7bb661]">${searchTerm}</span>`,
                  facet: `<span class="font-bold">${item.value.name}</span>`,
                }),
                value: item.value,
                count: item.count,
              })) || [],
            );
            setAutocompleteWords(
              response.autocompleteWords.map((word) => ({
                text: word.replace(new RegExp(`(${searchTerm})`, 'gi'), '<span class="text-[#7bb661]">$1</span>'),
                value: word,
              })) || [],
            );

            setSpellCheck(response.spellCheck || []);
          })
          .finally(() => {
            setLoading(false);
          });
      }, 300);
    }

    return () => {
      if (typingTimer.current) {
        clearTimeout(typingTimer.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const openSearchPage = (searchTerm: string, filter?: TaxonomyModel) => {
    const query: { [key: string]: string } = {
      searchTerm: searchTerm?.toLocaleLowerCase(),
    };
    if (filter) {
      query[filter.group] = filter.id;
    }
    const url = new URL(window.location.href);
    url.pathname = '/search';
    url.search = new URLSearchParams(query).toString();
    router.push(url);
  };

  return (
    <div className="flex w-full flex-col gap-2 py-7">
      {loading && (
        <div className="flex h-60 w-full items-center justify-center">
          <ProgressCircular indeterminate size={50} width={4} colorClassName="stroke-black" />
        </div>
      )}

      {!loading && !searchTerm && recommendations.recentSearches.length > 0 && !user?.isIpBasedIdentity && (
        <div className="flex flex-col">
          <div className="body-1 pt-3 pb-5 opacity-40">{t('search.$previoussearches')}</div>
          {recommendations.recentSearches.map((recentSearch, index) => (
            <button
              key={index}
              className="body-1 -ml-2 flex w-4/5 cursor-pointer justify-between rounded-xl px-2 py-3 text-left font-bold lowercase transition-colors hover:bg-gray-100"
              onClick={() => openSearchPage(recentSearch)}
            >
              {recentSearch}
            </button>
          ))}
        </div>
      )}

      {!loading && !searchTerm && recommendations.popularSeaches.length > 0 && (
        <div className="flex w-4/5 flex-wrap">
          <div className="body-1 w-full pt-3 pb-5 opacity-40">{t('search.$popularsearches')}</div>
          {recommendations.popularSeaches.map((popularSearch, index) => (
            <button
              key={index}
              className="body-1 -ml-2 flex w-4/12 cursor-pointer justify-between rounded-xl px-2 py-3 text-left font-bold lowercase transition-colors hover:bg-gray-100"
              onClick={() => openSearchPage(popularSearch?.toLocaleLowerCase())}
            >
              {popularSearch}
            </button>
          ))}
        </div>
      )}

      {!loading && searchTerm && (
        <div className="flex flex-col">
          {(autocompleteWords.length > 0 || topicSuggestions.length > 0) && (
            <>
              <div className="body-1 pt-3 pb-5 opacity-40">{t('search.$searchsuggestions')}</div>
              {topicSuggestions.map((suggestion) => (
                <button
                  key={suggestion.value.id}
                  className="body-1 -ml-2 flex w-4/5 cursor-pointer justify-between rounded-xl px-2 py-3 text-left transition-colors hover:bg-gray-100"
                  onClick={() => openSearchPage(searchTerm, suggestion.value)}
                >
                  <div dangerouslySetInnerHTML={{ __html: suggestion.text }} />
                  <div>{suggestion.count}</div>
                </button>
              ))}
              {autocompleteWords.map((word, index) => (
                <button
                  key={index}
                  className="body-1 -ml-2 w-4/5 cursor-pointer rounded-xl px-2 py-3 text-left transition-colors hover:bg-gray-100"
                  dangerouslySetInnerHTML={{ __html: word.text }}
                  onClick={() => openSearchPage(word.value)}
                />
              ))}
            </>
          )}
          {topicSuggestions.length > 0 && (
            <>
              <div className="body-1 pt-6 pb-4 opacity-40 first:pt-3">
                {t('search.$jumptodirectoryinnovationplatformortopic')}
              </div>
              {topicSuggestions.map((suggestion) => (
                <Link
                  key={suggestion.value.id}
                  link={suggestion.value.url}
                  className="body-1 -ml-2 flex w-4/5 cursor-pointer justify-between rounded-xl px-2 py-3 text-left font-bold transition-colors hover:bg-gray-100"
                >
                  {suggestion.value.name}
                </Link>
              ))}
            </>
          )}
        </div>
      )}

      {!loading && searchTerm && topicSuggestions.length === 0 && autocompleteWords.length === 0 && (
        <SearchNoResult searchTerm={searchTerm} spellCheck={spellCheck} />
      )}
    </div>
  );
};

export default AutocompleteContent;
